/** @format */
/** @format */

import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Select,
  Option,
  Table,
  TableColumn,
  Pagination,
  Tag,
  Message,
  Ellipsis
} from '@king-design/react'
import { MonitorSearch, TableSearch, getURLTableSearchParams } from 'src/components'
import { PrometheusAlarmTabProps } from 'src/pages/PrometheusMonitor/Alarm'
import { PageProps, initPage } from 'src/utils/interface'
import { useHistory } from 'react-router-dom'
import { useAsyncFn } from 'react-use'
import { getPrometheusAlerting, getPrometheusAlertPolicyList } from 'src/api/ApiPrometheusAlarm'

interface SearchProps extends PageProps {
  Instance: string
  PolicyName: string
}

const initSearch: SearchProps = {
  Instance: '',
  PolicyName: '',
  ...initPage
}

const PrometheusAlarming: FC<PrometheusAlarmTabProps> = ({ className, region, instances }) => {
  const history = useHistory()
  const [search, setSearch] = useState<SearchProps>(getURLTableSearchParams() || initSearch)

  const [{ value: tableData }, fetch] = useAsyncFn(
    async (params?) => {
      const result = await getPrometheusAlerting({
        ...search,
        ...params,
        Region: region
      })
      return {
        data: result?.Records || [],
        total: result?.TotalCount || 0
      }
    },
    [search, region]
  )

  useEffect(() => {
    fetch()
  }, [search, region])

  const toPolicyPage = async (policyID, ruleName?) => {
    if (policyID) {
      const result = await getPrometheusAlertPolicyList({
        PolicyID: parseInt(policyID)
      })
      if (result?.Polices?.length) {
        let url = `/monitor/prometheusmonitor/alarm/edit?tab=alarming&policyid=${policyID}`
        if (ruleName) {
          url += `&rulename=${ruleName}`
        }
        history.push(url)
      } else {
        Message.error('策略不存在')
      }
    } else {
      Message.error('策略ID为空')
    }
  }

  return (
    <div className={className}>
      <TableSearch params={search}>
        <div className="search_item">
          <p className="search_item_label">关联资源</p>
          <Select
            value={search.Instance}
            clearable
            filterable
            onChangeValue={value => {
              setSearch({
                ...search,
                Page: 1,
                Instance: value
              })
            }}
          >
            {instances?.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">策略名称</p>
          <MonitorSearch
            placeholder="请输入策略名称，支持模糊搜索"
            value={search.PolicyName}
            onChange={value => {
              setSearch({
                ...search,
                Page: 1,
                PolicyName: value
              })
            }}
          />
        </div>
      </TableSearch>
      <Table
        data={tableData?.data || []}
        animation={false}
        rowKey={data => data.ID}
        checkType="none"
      >
        <TableColumn
          key="InstanceID"
          title="关联资源"
          slotTemplate={([data]) => {
            return data.InstanceID ? (
              <>
                <p>{data.InstanceName}</p>
                <Ellipsis className="table_text" maxLines={2} hoverable>
                  {data.InstanceID}
                </Ellipsis>
              </>
            ) : null
          }}
        ></TableColumn>
        <TableColumn
          key="RuleName"
          title="策略 / 规则"
          slotTemplate={([data]) => {
            return (
              <a
                onClick={() => {
                  toPolicyPage(data.PolicyID, data.RuleName)
                }}
              >{`${data.PolicyName} / ${data.RuleName}`}</a>
            )
          }}
        />
        <TableColumn key="CreatedAt" title="触发时间" width="100"></TableColumn>
        <TableColumn
          key="AlertStatus"
          title="告警状态"
          width="100"
          slotTemplate={([data]) => {
            switch (data.AlertStatus) {
              case 'firing':
                return <Tag type="danger">告警中</Tag>
              case 'silenced':
                return <Tag type="warning">静默</Tag>
              case 'inhibited':
                return <Tag type="warning">抑制</Tag>
              case 'silenced and inhibited':
                return <Tag type="warning">静默且抑制</Tag>
              default:
                // 没有
                return <Tag type="default">已恢复</Tag>
            }
          }}
        ></TableColumn>
        <TableColumn
          key="Labels"
          title="标签"
          slotTemplate={([data]) => {
            if (!data.Labels || !Array.isArray(data.Labels)) return '-'
            return (
              <div className="label-tags">
                {data.Labels.map((label, index) => {
                  if (label.Name === 'severity') {
                    return <p key={label.Name}>告警等级（severity）= {label.Value}</p>
                  } else {
                    return (
                      <p key={label.Name}>
                        {label.Name} = {label.Value}
                      </p>
                    )
                  }
                })}
              </div>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Summary"
          title="告警摘要"
          slotTemplate={([data]) => {
            return (
              <Ellipsis maxLines={2} hoverable>
                {data.Summary}
              </Ellipsis>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Description"
          title="告警内容"
          slotTemplate={([data]) => {
            return (
              <Ellipsis maxLines={2} hoverable>
                {data.Description}
              </Ellipsis>
            )
          }}
        ></TableColumn>
        {/* <TableColumn
          key="Recipients"
          title="通知对象"
          width="120"
          slotTemplate={([data]) => {
            const { NotificationTargets } = data
            const { Recipients = [], Webhooks = [] } = NotificationTargets || {}
            return Recipients?.length || Webhooks?.length ? (
              <div className="ksc-space">
                {Recipients?.length ? (
                  <DeleteConfirm
                    title="联系人"
                    content={
                      <>
                        <TablePage
                          tableProps={{
                            checkType: 'none',
                            rowKey: data => data.ContactID,
                            scheme: notificationResultScheme,
                            data: Recipients
                          }}
                        ></TablePage>
                      </>
                    }
                  >
                    <a>联系人</a>
                  </DeleteConfirm>
                ) : null}
                {Webhooks?.length ? (
                  <DeleteConfirm
                    content={
                      <TablePage
                        tableProps={{
                          checkType: 'none',
                          rowKey: data => data.ContactID,
                          scheme: urlNoticeResultScheme,
                          data: Webhooks
                        }}
                      ></TablePage>
                    }
                  >
                    <a>回调</a>
                  </DeleteConfirm>
                ) : null}
              </div>
            ) : (
              '-'
            )
          }}
        ></TableColumn> */}
      </Table>
      <Pagination
        value={search.Page}
        limit={search.PageSize}
        total={tableData?.total || 0}
        size="small"
        onChangeValue={value => {
          setSearch({
            ...search,
            Page: value
          })
        }}
        onChangeLimit={limit => {
          setSearch({
            ...search,
            PageSize: limit
          })
        }}
      />
    </div>
  )
}

export default styled(PrometheusAlarming)``
